"use client"

import { useEffect, useState, useRef, useCallback } from "react"
import { requestTokenFromParent } from "@/lib/app-messaging"
import { useAuth } from "@/contexts/auth-context"
import { logger } from "@/lib/logger"
import { useCommissionDashboard } from '@/lib/commission-dashboard-service'
import { CommissionDashboardContent } from '@/components/dashboard/CommissionDashboardContent'
import { useLanguage } from "@/contexts/language-context"
import { formatDate } from "@/lib/formatting"
import { useNavigation } from "@/contexts/navigation-context"
import dynamic from "next/dynamic"

// Dynamically import views to reduce initial bundle size
const PrepareView = dynamic(() => import('@/components/prepare/PrepareView'), { 
  loading: () => <div className="flex items-center justify-center min-h-screen">Loading...</div>
})
const HistoryView = dynamic(() => import('@/components/history/HistoryView'), {
  loading: () => <div className="flex items-center justify-center min-h-screen">Loading...</div>
})
const SummaryView = dynamic(() => import('@/components/summary/SummaryView'), {
  loading: () => <div className="flex items-center justify-center min-h-screen">Loading...</div>
})

export default function Home() {
  const { token, isAuthenticated } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [lastPayoutText, setLastPayoutText] = useState("")
  const [processedDateText, setProcessedDateText] = useState("")
  const { getLatestPayout } = useCommissionDashboard()
  const { dictionary, language } = useLanguage()
  const initialLoadComplete = useRef(false)
  const latestPayoutRef = useRef<any>(null)
  const { currentView } = useNavigation()
  const previousView = useRef<string | null>(null)

  // Memoize the format function to prevent unnecessary recalculations
  const formatPayoutTexts = useCallback((latestPayout: any) => {
    const none = dictionary?.dashboard?.cards?.lastPayout?.none ?? "None"
    const processedOn = dictionary?.dashboard?.cards?.lastPayout?.processedOn ?? "Processed on"

    const newLastPayoutText = latestPayout?.payoutDate
      ? formatDate(latestPayout.payoutDate, 'MMMM d, yyyy', language)
      : none

    const newProcessedDateText = latestPayout?.createdDatetime
      ? `${processedOn} ${formatDate(latestPayout.createdDatetime, 'MMMM d, yyyy', language)}`
      : ''

    return { newLastPayoutText, newProcessedDateText }
  }, [dictionary, language])

  // Request token from parent window when app is initialized
  useEffect(() => {
    // Make sure we're in a browser environment
    if (typeof window !== "undefined") {
      // Use a small timeout to ensure the app is fully rendered
      const timer = setTimeout(() => {
        requestTokenFromParent()
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [])

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true)
    try {
      const latestPayout = await getLatestPayout()

      latestPayoutRef.current = latestPayout

      const { newLastPayoutText, newProcessedDateText } = formatPayoutTexts(latestPayout)
      setLastPayoutText(newLastPayoutText)
      setProcessedDateText(newProcessedDateText)
    } catch (error) {
      logger.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [getLatestPayout, formatPayoutTexts])

  // Initial data load and navigation handling
  useEffect(() => {
    if (!token || !isAuthenticated) return

    const shouldFetchData = 
      (!initialLoadComplete.current && currentView === 'dashboard') || // First dashboard load
      (currentView === 'dashboard' && (previousView.current === 'history' || previousView.current === 'prepare')) // Coming back from history or prepare

    if (shouldFetchData) {
      fetchDashboardData()
      initialLoadComplete.current = true
    }

    // Update previous view for navigation tracking
    previousView.current = currentView
  }, [token, isAuthenticated, fetchDashboardData, currentView])

  // Update texts when dictionary or language changes
  useEffect(() => {
    if (!token || !isAuthenticated || !latestPayoutRef.current) return
    
    const { newLastPayoutText, newProcessedDateText } = formatPayoutTexts(latestPayoutRef.current)
    setLastPayoutText(newLastPayoutText)
    setProcessedDateText(newProcessedDateText)
  }, [token, isAuthenticated, formatPayoutTexts])

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      {dictionary?.app?.loading?.general ?? "Loading..."}
    </div>
  }

  // Render different views based on the current view parameter
  switch (currentView) {
    case 'prepare':
      return <PrepareView />
    case 'history':
      return <HistoryView />
    case 'summary':
      return <SummaryView />
    default:
      return (
        <CommissionDashboardContent
          lastPayoutText={lastPayoutText}
          processedDateText={processedDateText}
        />
      )
  }
}
